// Frister och påminnelser: läses från project_reminders, som fylls på av den
// nattliga funktionen project-reminders. Går även att köra manuellt här.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlarmClock, CheckCircle2, RefreshCw } from "lucide-react";

interface Props {
  projectId: string;
  onOpenTab?: (tab: string) => void;
}

interface ReminderRow {
  id: string;
  title: string;
  detail: string | null;
  severity: string;
  due_date: string | null;
  target_tab: string | null;
}

const fmt = (d: string) => new Date(d).toLocaleDateString("sv-SE");

const DeadlinesPanel: React.FC<Props> = ({ projectId, onOpenTab }) => {
  const [items, setItems] = useState<ReminderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("project_reminders")
      .select("id, title, detail, severity, due_date, target_tab")
      .eq("project_id", projectId)
      .is("resolved_at", null)
      .is("dismissed_at", null)
      .order("severity", { ascending: true })
      .limit(50);
    setItems((data || []) as ReminderRow[]);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const runCheck = async () => {
    setChecking(true);
    try {
      await supabase.functions.invoke("project-reminders", {
        body: { project_id: projectId },
      });
      await load();
    } finally {
      setChecking(false);
    }
  };

  const dismiss = async (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    await supabase
      .from("project_reminders")
      .update({ dismissed_at: new Date().toISOString() })
      .eq("id", id);
  };

  const overdue = items.filter((i) => i.severity === "overdue").length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <AlarmClock className="h-4 w-4" /> Frister och påminnelser
        </CardTitle>
        <div className="flex items-center gap-2">
          <Badge variant={overdue ? "destructive" : "secondary"}>
            {overdue ? `${overdue} försenade` : "Inget försenat"}
          </Badge>
          <Button size="sm" variant="ghost" onClick={runCheck} disabled={checking}>
            <RefreshCw className={`h-4 w-4 ${checking ? "animate-spin" : ""}`} />
            <span className="ml-1 hidden sm:inline">Kontrollera nu</span>
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Kontrolleras automatiskt varje natt: skyddsronder, bevis som går ut, öppna
          åtgärder och okvitterade lagkrav.
        </p>
        {loading && <p className="text-sm text-muted-foreground">Laddar…</p>}
        {!loading && items.length === 0 && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4" /> Inget förfaller just nu.
          </p>
        )}
        {items.map((i) => (
          <div
            key={i.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium">{i.title}</p>
              <p className="text-xs text-muted-foreground">
                {i.detail}
                {i.due_date ? ` · Sista dag ${fmt(i.due_date)}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={i.severity === "overdue" ? "destructive" : "outline"}>
                {i.severity === "overdue" ? "Försenad" : "Snart"}
              </Badge>
              {i.target_tab && onOpenTab && (
                <Button size="sm" variant="ghost" onClick={() => onOpenTab(i.target_tab!)}>
                  Öppna
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => dismiss(i.id)}>
                Dölj
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default DeadlinesPanel;
